// The real active-case context strip — fills the F-SHELL `CaseStripSlot`
// (plan §2.1). Rebuilds the throwaway mockup `components/PaneCase.tsx` as a
// Tailwind + logical-RTL + i18n surface, consuming `useCaseStore` VERBATIM
// (no new store, no new BE wire).
//
// Invariants:
//   W1 — no business logic. The strip lists / selects / anchors only; the
//        jurisdiction tint is presentation (`case-jurisdiction`, §2.7).
//   W2 — no Office.js. The XmlPart read stays in the store's initialize().
//   W5 — no new store. `useCaseStore` is already wired into useAuth.logout().
//   W6 — no $/tokens.
//   §9 — logical RTL utilities; BE client names get `bidiProps` (in the strip
//        header + inside CasePicker).
//
// The F6 display-name gap (plan §2.5): the XmlPart anchor carries only ids, so
// the strip resolves the active case NAME from the loaded list
// (`loadCases()` + `cases.find`). While the list is in flight a bound doc
// shows the "Dossier lié" fallback, then self-heals once it resolves. Zero new
// BE call. A revoked-access case stays in fallback forever — an honest render
// (the BE access matrix is the source of truth, no client-side orphan logic).

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCaseStore } from '@/ui/stores/use-case-store'
import { bidiProps } from '@/lib/bidi'
import type { NextEventSummary } from '@/domain/case/entity'
import { CasePicker, type PickerStep } from './CasePicker'
import { JurisdictionBadge } from './JurisdictionBadge'

// The next-échéance pill ("J-6"). `daysUntil` is BE-computed (W1 — never a
// client-side date delta); `importance` is a BE presentation hint that drives
// the urgency tint (warn/amber for 'high'), the same way the jurisdiction tint
// is presentation. The event title is BE free-text → exposed as the accessible
// label / native tooltip rather than inlined (the strip is space-constrained).
function NextEventBadge({ event }: { event: NextEventSummary }): JSX.Element {
  const { t } = useTranslation()
  const urgent = event.importance === 'high'
  return (
    <span
      data-testid="case-next-event"
      title={event.title}
      aria-label={t('case.nextEvent.aria', {
        days: event.daysUntil,
        title: event.title,
      })}
      className={[
        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold',
        urgent ? 'bg-warn/15 text-warn' : 'bg-ink/5 text-ink/70',
      ].join(' ')}
    >
      {t('case.nextEvent.badge', { days: event.daysUntil })}
    </span>
  )
}

export function CaseStrip(): JSX.Element {
  const { t } = useTranslation()

  // Narrow selectors (CLAUDE.md / FE selector discipline) — never the whole
  // store object.
  const cases = useCaseStore((s) => s.cases)
  const briefs = useCaseStore((s) => s.briefs)
  const activeContext = useCaseStore((s) => s.activeContext)
  const loading = useCaseStore((s) => s.loading)
  const loadCases = useCaseStore((s) => s.loadCases)
  const selectCase = useCaseStore((s) => s.selectCase)
  const selectBrief = useCaseStore((s) => s.selectBrief)

  // Picker open/step are pure presentation — local state, no store (plan §2.5
  // / §11 C5, as the mockup did).
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<PickerStep>('cases')

  // Load the case list once so the picker is ready + the bound case's name can
  // be resolved from its id (F6 — XmlPart carries only ids).
  useEffect(() => {
    if (cases.length === 0) void loadCases()
  }, [cases.length, loadCases])

  const activeCase =
    activeContext != null
      ? (cases.find((c) => c.id === activeContext.caseId) ?? null)
      : null

  const togglePicker = (): void => {
    setStep('cases')
    setOpen((o) => !o)
  }

  const onPickCase = (caseId: string): void => {
    void selectCase(caseId)
    setStep('briefs')
  }

  const onPickBrief = (briefId: string): void => {
    selectBrief(briefId)
    setOpen(false)
  }

  // ── The 4-state header (plan §2.3) ────────────────────────────────────────
  // State 1 — no case bound.
  let header: JSX.Element
  if (activeContext == null) {
    header = (
      <div
        data-testid="case-strip-empty"
        className="flex min-w-0 flex-1 flex-col"
      >
        <span className="truncate text-xs font-semibold text-ink/80">
          {t('case.empty.title')}
        </span>
        <span className="truncate text-[10px] text-ink/50">
          {t('case.empty.hint')}
        </span>
      </div>
    )
  } else if (activeCase == null) {
    // State 2 — bound but resolving (F6 fallback). Self-heals once loadCases
    // resolves; stays here forever for a revoked-access case (honest render).
    header = (
      <div
        data-testid="case-strip-bound-fallback"
        className="flex min-w-0 flex-1 flex-col"
      >
        <span className="truncate text-xs font-semibold text-ink/80">
          {t('case.boundFallback.title')}
        </span>
        <span className="truncate text-[10px] text-ink/50">
          {t('case.boundFallback.meta', {
            briefId: activeContext.briefId.slice(0, 8),
            version: activeContext.snapshotVersion,
          })}
        </span>
      </div>
    )
  } else {
    // State 3 — bound + resolved.
    header = (
      <div
        data-testid="case-strip-resolved"
        className="flex min-w-0 flex-1 items-center gap-2"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className="truncate text-xs font-semibold text-ink"
            {...bidiProps(activeCase.clientName)}
          >
            {activeCase.clientName}
          </span>
          <span
            className="truncate text-[10px] text-ink/60"
            {...bidiProps(activeCase.caseType)}
          >
            {activeCase.caseType}
          </span>
        </div>
        {activeCase.nextEvent != null && (
          <NextEventBadge event={activeCase.nextEvent} />
        )}
        <JurisdictionBadge code={activeCase.jurisdictionPrimary} />
      </div>
    )
  }

  return (
    <div
      data-testid="shell-case-strip"
      className="relative flex items-center gap-2 border-b border-ink/10 bg-ink-soft/[0.03] px-3 py-2"
    >
      <span
        aria-hidden="true"
        className="h-7 w-0.5 shrink-0 rounded-full bg-ink/20"
      />
      {header}
      <button
        type="button"
        data-testid="case-strip-trigger"
        onClick={togglePicker}
        className="shrink-0 rounded border border-ink/15 px-2 py-0.5 text-[10px] font-semibold text-ink hover:bg-ink/5"
      >
        {activeContext != null ? t('case.action.change') : t('case.action.choose')}
        <span aria-hidden="true" className="ms-0.5 inline-block rtl:-scale-x-100">
          ▾
        </span>
      </button>

      {open && (
        // State 4 (loading) is rendered inside the picker body.
        <CasePicker
          step={step}
          loading={loading}
          cases={cases}
          briefs={briefs}
          onPickCase={onPickCase}
          onPickBrief={onPickBrief}
          onBack={() => setStep('cases')}
        />
      )}
    </div>
  )
}
